/**
 * Player Status Panel component - displays player avatars and current turn
 */

import React from 'react';
import { IPlayerStatusPanelProps } from '../types';
import { useAssets } from '../utils/assets-context';
import './player-status-panel.scss';

export const PlayerStatusPanel: React.FC<IPlayerStatusPanelProps> = ({
  player1,
  player2,
  currentPlayer,
  gameStatus,
  position,
}) => {
  const { avatarImages, isLoading } = useAssets();
  const renderPlayerInfo = (player: any, isCurrentPlayer: boolean) => {
    if (!player) return null;

    const playerClassName = `player-status-panel__player ${
      isCurrentPlayer ? 'player-status-panel__player--active' : ''
    }`;

    return (
      <div key={player.id} className={playerClassName} data-testid={`player-${player.id}-info`}>
        <div className='player-status-panel__avatar-container'>
          <div
            className='player-status-panel__avatar'
            style={{
              backgroundColor: player.color === 'red' ? '#e74c3c' : '#f1c40f',
            }}
          >
            {player.avatar.imageUrl && avatarImages[player.avatar.imageUrl] ? (
              // Display avatar image
              <img
                src={avatarImages[player.avatar.imageUrl]}
                alt={`${player.name}'s avatar`}
                className='avatar-image'
              />
            ) : (
              // Fallback: show a default icon if image fails to load
              <div className='avatar-fallback'>
                <span>👤</span>
              </div>
            )}
          </div>

          {isCurrentPlayer && gameStatus === 'in-progress' && (
            <div className='player-status-panel__turn-indicator'>
              <span>Your Turn!</span>
            </div>
          )}
        </div>

        <div className='player-status-panel__info'>
          <h3 className='player-status-panel__name'>{player.name}</h3>
          <div
            className={`player-status-panel__piece player-status-panel__piece--${player.color}`}
          />
        </div>
      </div>
    );
  };

  // Based on position, show only one player
  if (position === 'left') {
    return player1 ? renderPlayerInfo(player1, currentPlayer === 1) : null;
  } else if (position === 'right') {
    return player2 ? renderPlayerInfo(player2, currentPlayer === 2) : null;
  }

  // Default behavior for backward compatibility
  return (
    <>
      {player1 && (
        <div
          className='player-status-panel__player-container'
          data-testid='player-status-panel-left'
        >
          {renderPlayerInfo(player1, currentPlayer === 1)}
        </div>
      )}

      {player2 && (
        <div
          className='player-status-panel__player-container'
          data-testid='player-status-panel-right'
        >
          {renderPlayerInfo(player2, currentPlayer === 2)}
        </div>
      )}

      {gameStatus === 'won' && (
        <div className='player-status-panel__game-message player-status-panel__game-message--winner'>
          <div className='celebration-message'>🎉 Player {currentPlayer} Wins! 🎉</div>
        </div>
      )}

      {gameStatus === 'draw' && (
        <div className='player-status-panel__game-message player-status-panel__game-message--draw'>
          <div className='draw-message'>🤝 It's a Draw! 🤝</div>
        </div>
      )}
    </>
  );
};
